import { ArrowRight } from 'lucide-react'
import { Controller, type SubmitHandler, type UseFormReturn } from 'react-hook-form'

import { FormTextFieldDS } from 'components/reusable/form/FormFields'
import { BorderedRadioButton, Button, ButtonGroup, Checkbox, CheckboxGroup, DateInput, RadioGroup, TextInput } from 'slab'
import { PostQuotingProspectsReasonsForChangingInsuranceItem, type PutQuotingProspectsIdReasonsForChangingInsuranceItem } from 'utils/web_api.schemas'

export type BusinessFormData = {
  businessName: string
  offersBenefits?: boolean | null
  coverageStartDate: string
  reasons: Record<PutQuotingProspectsIdReasonsForChangingInsuranceItem | 'other', boolean>
  otherReason: string
}

export const READABLE_CHANGE_REASON = {
  [PostQuotingProspectsReasonsForChangingInsuranceItem.make_employees_happier]: 'Making sure my employees are happy',
  [PostQuotingProspectsReasonsForChangingInsuranceItem.avoid_rate_increases]: 'Avoiding unexpected rate increases',
  [PostQuotingProspectsReasonsForChangingInsuranceItem.best_benefits]: 'Providing the best benefits that I can afford',
  [PostQuotingProspectsReasonsForChangingInsuranceItem.visibility_control_of_healthcare_costs]: 'Visibility and control over healthcare costs'
}

interface BusinessDetailsFormProps {
  form: UseFormReturn<BusinessFormData>
  title: string
  backText: string
  submitText: string
  onBack?: () => void
  onSubmit?: SubmitHandler<BusinessFormData>
}

export function BusinessDetailsForm ({
  form,
  title,
  backText,
  submitText,
  onBack = () => {},
  onSubmit = () => {}
}: BusinessDetailsFormProps) {
  const {
    register,
    handleSubmit,
    control,
    watch,
    formState: { errors, isValid },
  } = form

  return (
    <form onSubmit={handleSubmit(onSubmit)} className='flex flex-col gap-10 max-w-3xl mx-auto'>
      <h1 className='text-2xl font-medium text-gray-800'>{title}</h1>
      <div className='flex flex-col gap-6'>
        <FormTextFieldDS
          fieldId='businessName'
          fieldLabel='Business name'
          placeholder='Business name'
          register={register}
          error={errors.businessName}
          validationRule={{ required: 'Business name is required' }}
        />

        <Controller
          name='offersBenefits'
          control={control}
          rules={{ validate: (value) => value !== undefined || 'Please select an option' }}
          render={({ field }) => (

            <RadioGroup
              name='offersBenefits'
              layout='horizontal'
              label='Does this business currently offer benefits?'
              error={errors.offersBenefits?.message}
            >
              <BorderedRadioButton
                size='small'
                label='Yes'
                checked={field.value === true}
                onChange={() => field.onChange(true)}
              />
              <BorderedRadioButton
                size='small'
                label='No'
                checked={field.value !== true}
                onChange={() => field.onChange(false)}
              />
            </RadioGroup>
          )}
        />

        <div className='max-w-48'>
          <DateInput
            {...register('coverageStartDate', {
              required: 'Coverage start date is required',
              setValueAs: (value) => value
                ? new Date(value).toISOString().split('T')[0]
                : undefined,
              validate: (value) => {
                if (!value) return true
                const selectedDate = new Date(value)
                const today = new Date()
                today.setHours(0, 0, 0, 0)
                return selectedDate >= today || 'Coverage start date cannot be in the past'
              }
            })}
            label='Expected coverage start date'
            error={errors.coverageStartDate?.message}
          />
        </div>

        <CheckboxGroup
          label='Why are they looking to change?'
          gap={3}
        >
          <Checkbox
            {...register('reasons.make_employees_happier')}
            label={READABLE_CHANGE_REASON[PostQuotingProspectsReasonsForChangingInsuranceItem.make_employees_happier]}
          />
          <Checkbox
            {...register('reasons.avoid_rate_increases')}
            label={READABLE_CHANGE_REASON[PostQuotingProspectsReasonsForChangingInsuranceItem.avoid_rate_increases]}
          />
          <Checkbox
            {...register('reasons.best_benefits')}
            label={READABLE_CHANGE_REASON[PostQuotingProspectsReasonsForChangingInsuranceItem.best_benefits]}
          />
          <Checkbox
            {...register('reasons.visibility_control_of_healthcare_costs')}
            label={READABLE_CHANGE_REASON[PostQuotingProspectsReasonsForChangingInsuranceItem.visibility_control_of_healthcare_costs]}
          />
          <Checkbox
            {...register('reasons.other')}
            label='Other (specify below)'
          />
          {watch('reasons.other') && (
            <TextInput
              {...register('otherReason', {
                required: 'Other reason is required when Other is selected'
              })}
              placeholder='Other reason'
              maxLength={256}
            />
          )}
        </CheckboxGroup>
      </div>

      <ButtonGroup align='right'>
        <Button
          size='large'
          variant='secondary'
          onClick={onBack}
        >
          {backText}
        </Button>
        <Button
          size='large'
          variant='primary'
          type='submit'
          rightIcon={ArrowRight}
          disabled={!isValid}
        >
          {submitText}
        </Button>
      </ButtonGroup>
    </form>
  )
}
